import { useSsoStart } from '@/api/mutations/useAuth';
import { CONTACT_EMAIL } from '@/constants';
import { cn, Button, Input } from '@bliks-io/ui';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { PolicyDialog } from './policyDialog';
import { PrivacyContent } from './privacyContent';
import { TermsContent } from './termsContent';
import { useBrowserRedirect } from '@/lib/useBrowserRedirect';
import { translateErrorMessage } from '@/lib/errors/error-formatter';
import type { ApiError } from '@/lib/agent.types';

interface LoginFormData {
  email: string;
}

interface LoginFormProps {
  /** From the hosted login's login_hint after an invite or password reset. */
  prefillEmail?: string;
}

export function LoginForm({ prefillEmail }: LoginFormProps) {
  const { t } = useTranslation();
  const ssoStartMutation = useSsoStart();
  const { isRedirecting, redirect } = useBrowserRedirect();

  // Dialog state for Terms of Service and Privacy Policy
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    clearErrors
  } = useForm<LoginFormData>({
    defaultValues: {
      email: prefillEmail ?? ''
    }
  });

  // The unknown-email notice binds to the email it was answered for; editing
  // the address clears it so a stale notice never shadows a fresh attempt.
  const currentEmail = watch('email');
  const emailUnknown =
    ssoStartMutation.data?.mode === 'unknown' && currentEmail === ssoStartMutation.variables?.email;

  const onSubmit = async (data: LoginFormData) => {
    clearErrors();

    try {
      const start = await ssoStartMutation.mutateAsync({ email: data.email });
      if (start.mode === 'sso') {
        redirect(start.authorizationUrl);
      }
      // 'unknown' renders the neutral notice via the derived state above
    } catch (error) {
      const message = translateErrorMessage(error as unknown as ApiError, t);
      setError('root', { type: 'manual', message });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full gap-6">
      <div className="grid gap-6">
        {/* Email Field */}
        <div className="grid gap-2">
          <Input
            id="email"
            type="email"
            placeholder={t('email')}
            {...register('email', {
              required: t('emailIsRequired')
            })}
            className={cn(errors.email && 'border-destructive')}
          />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>

        {/* Unknown-email notice — deliberately neutral, no account enumeration */}
        {emailUnknown && (
          <div className="p-2 text-sm text-center rounded-md text-muted-foreground bg-muted">
            {t('noWorkspaceForEmail')}
          </div>
        )}

        {/* Error Message */}
        {errors.root && (
          <div className="p-2 text-sm text-center rounded-md text-destructive bg-destructive/10">
            {errors.root.message}
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full"
          loading={ssoStartMutation.isPending || isRedirecting}
        >
          {t('common.continue')}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      <div className="text-sm text-center text-muted-foreground">
        {t('dontHaveAccount')}?{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-foreground hover:underline">
          {t('contactCompany')}
        </a>
      </div>

      <div className="text-xs text-center opacity-60 text-muted-foreground">
        <Trans
          i18nKey="byContinueTermFull"
          components={{
            terms: (
              <Button
                type="button"
                variant="link"
                onClick={() => setTermsDialogOpen(true)}
                className="h-auto p-0 text-xs underline text-muted-foreground hover:text-foreground"
              />
            ),
            privacy: (
              <Button
                type="button"
                variant="link"
                onClick={() => setPrivacyDialogOpen(true)}
                className="h-auto p-0 text-xs underline text-muted-foreground hover:text-foreground"
              />
            )
          }}
        />
      </div>

      {/* Policy Dialogs */}
      <PolicyDialog
        open={termsDialogOpen}
        onOpenChange={setTermsDialogOpen}
        title={t('termOfService')}
        content={<TermsContent />}
      />

      <PolicyDialog
        open={privacyDialogOpen}
        onOpenChange={setPrivacyDialogOpen}
        title={t('privacyPolicy')}
        content={<PrivacyContent />}
      />
    </form>
  );
}
